import React, { useState, useRef, useEffect } from 'react';
import { Trade } from '../api/client';

interface SearchableTradeDropdownProps {
  trades: Trade[];
  selectedTradeId: number | '';
  onChange: (tradeId: number | null) => void;
  placeholder?: string;
}

export function SearchableTradeDropdown({
  trades,
  selectedTradeId,
  onChange,
  placeholder = "Search trades..."
}: SearchableTradeDropdownProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get selected trade name for display
  const selectedTrade = trades.find(t => t.id === selectedTradeId);
  const displayValue = selectedTrade ? selectedTrade.name : searchTerm;

  // Filter trades based on search
  const filteredTrades = trades.filter(trade =>
    trade.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle trade selection
  const handleSelect = (trade: Trade) => {
    onChange(trade.id);
    setSearchTerm('');
    setIsOpen(false);
  };

  // Handle clearing selection
  const handleClear = () => {
    onChange(null);
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          type="text"
          value={isOpen ? searchTerm : (selectedTrade?.name || '')}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            if (selectedTrade) {
              setSearchTerm(selectedTrade.name);
            }
          }}
          placeholder={placeholder}
          style={{
            flex: 1,
            padding: '10px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />
        {selectedTrade && (
          <button
            onClick={handleClear}
            style={{
              padding: '10px 16px',
              background: '#f0f0f0',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Clear
          </button>
        )}
      </div>

      {isOpen && filteredTrades.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: '4px',
          marginTop: '4px',
          maxHeight: '200px',
          overflow: 'auto',
          zIndex: 1001,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {filteredTrades.map(trade => (
            <div
              key={trade.id}
              onClick={() => handleSelect(trade)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                fontSize: '14px',
                background: selectedTradeId === trade.id ? '#f0f8ff' : '#fff'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = selectedTradeId === trade.id ? '#f0f8ff' : '#fff';
              }}
            >
              {trade.name}
            </div>
          ))}
        </div>
      )}

      {isOpen && filteredTrades.length === 0 && searchTerm && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: '4px',
          marginTop: '4px',
          padding: '10px 12px',
          fontSize: '14px',
          color: '#666',
          zIndex: 1001,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          No trades found
        </div>
      )}
    </div>
  );
}
